<script setup lang="ts">
/**
 * The one dialog that stands between a tap and any spend.
 *
 * Rendered once near the root in `App.vue`, beside `ConfirmSignOut`, and
 * driven entirely by `state/confirm` — it has no props, which is what lets
 * every buy, research node and XP conversion share it. The title and body are
 * plain strings the caller has already translated; this dialog only owns its
 * own chrome (the window, Cancel, the default Buy caption) and the price row.
 *
 * The same three rules as `ConfirmSignOut`, and for the same reasons.
 *
 * **Escape and the backdrop both decline**, and the keydown is stopped rather
 * than bubbled, so a host screen's own Escape listener does not also react.
 *
 * **Focus moves to Cancel, and the confirm plate is not autofocused** — a
 * Return key on an autofocused confirm is exactly the accident this exists
 * to prevent.
 *
 * **A `danger` request is the red plate**, same as Sign out; otherwise the
 * plate is green (`gem`) when any price line spends seals, or the ordinary
 * yellow (`primary`) when it spends credits, XP, or nothing at all.
 *
 * It is a small game window (`GameWindow`): the caller's art if any, the
 * title in game lettering, an optional line of body copy, the price as chips
 * with their coin or seal icons, and the two answers on plates beneath.
 */
import { computed, nextTick, ref, watch, type ComponentPublicInstance } from 'vue';

import { useI18n } from '@/i18n';
import { confirmNo, confirmRequest, confirmYes } from '@/state/confirm';
import GameIcon from './kit/GameIcon.vue';
import GameWindow from './kit/GameWindow.vue';
import type { GameIconName } from './kit/icons';
import ActionButton from './panel/ActionButton.vue';
import SheetTransition from './panel/SheetTransition.vue';

const { t, n } = useI18n();

/** Cancel is a component; its root element is the button that takes focus. */
const cancelButton = ref<ComponentPublicInstance | null>(null);

watch(confirmRequest, async (req) => {
  if (!req) return;
  await nextTick();
  (cancelButton.value?.$el as HTMLElement | undefined)?.focus?.();
});

/** The confirm plate's paint: danger wins outright, then whichever currency the spend is in. */
const variant = computed<'danger' | 'gem' | 'primary'>(() => {
  const req = confirmRequest.value;
  if (!req) return 'primary';
  if (req.danger) return 'danger';
  return req.price?.some((p) => p.currency === 'diamonds') ? 'gem' : 'primary';
});

/** The window's icon follows what is at stake. */
const windowIcon = computed<GameIconName>(() => {
  const req = confirmRequest.value;
  if (req?.danger) return 'shield';
  const currency = req?.price?.[0]?.currency;
  return currency === 'diamonds' ? 'seal' : currency === 'xp' ? 'star' : 'credits';
});

const PRICE_ICON = { credits: 'credits', diamonds: 'seal', xp: 'star' } as const satisfies Record<string, GameIconName>;

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  // See the note above: a host's window-level Escape listener must not see this.
  event.stopPropagation();
  confirmNo();
}
</script>

<template>
  <SheetTransition>
    <GameWindow
      v-if="confirmRequest"
      width="24rem"
      level="alert"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-sheet-title"
      data-test="confirm-sheet"
      :title="t('confirm.title')"
      :icon="windowIcon"
      @close="confirmNo()"
      @keydown="onKeydown"
    >
      <div class="content">
        <img v-if="confirmRequest.art" class="art" :src="confirmRequest.art" alt="" aria-hidden="true" />
        <h3 id="confirm-sheet-title" class="k-title heading">{{ confirmRequest.title }}</h3>
        <p v-if="confirmRequest.body" class="says">{{ confirmRequest.body }}</p>
        <div v-if="confirmRequest.price?.length" class="price" data-test="confirm-price">
          <span v-for="(p, i) in confirmRequest.price" :key="i" class="k-chip price-line">
            <GameIcon :name="PRICE_ICON[p.currency]" :size="30" />
            <span>{{ n(p.amount) }}</span>
            <span v-if="p.currency === 'xp'" class="xp-unit">{{ t('confirm.xpUnit') }}</span>
          </span>
        </div>
      </div>

      <template #actions>
        <ActionButton ref="cancelButton" size="sm" data-test="confirm-cancel" @click="confirmNo()">
          {{ t('confirm.cancel') }}
        </ActionButton>
        <ActionButton :variant="variant" size="sm" data-test="confirm-yes" @click="confirmYes()">
          {{ confirmRequest.confirmLabel ?? t('confirm.buy') }}
        </ActionButton>
      </template>
    </GameWindow>
  </SheetTransition>
</template>

<style scoped>
.content {
  display: grid;
  justify-items: center;
  gap: 10px;
  text-align: center;
}

.art {
  width: 100%;
  max-height: 7rem;
  object-fit: contain;
}

.heading {
  margin: 0;
  font-size: 1.4rem;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.says {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.45;
  color: #dde6ee;
}

.price {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.price-line {
  font-size: 1.25rem;
  padding-right: 0.8rem;
}

.xp-unit {
  font-family: var(--font-game-body);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--kit-gold);
}
</style>
